// Wrapper around GTM-injected window.posthog
declare global {
  interface Window {
    posthog?: any;
  }
}

// Get PostHog instance
export const getPostHog = () => {
  if (typeof window !== 'undefined' && typeof window.posthog !== 'undefined') {
    return window.posthog;
  }
  return null;
};

// Server-side PostHog instance
let serverPostHog: any = null;

const getServerPostHog = async () => {
  if (!serverPostHog && process.env.NEXT_PUBLIC_POSTHOG_KEY && typeof window === 'undefined') {
    try {
      const { PostHog } = await import('posthog-node');
      serverPostHog = new PostHog(
        process.env.NEXT_PUBLIC_POSTHOG_KEY,
        {
          host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
        }
      );
    } catch (error) {
      // PostHog Node.js library not available
    }
  }
  return serverPostHog;
};

// API Event tracking functions
export const trackAPIEvent = async (eventName: string, properties: Record<string, any>) => {
  const eventData = {
    ...properties,
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV,
  };
  
  if (typeof window === 'undefined') {
    // Server-side tracking
    const serverPostHog = await getServerPostHog();
    if (serverPostHog) {
      serverPostHog.capture({
        distinctId: properties.userEmail || properties.userId?.toString() || 'anonymous',
        event: eventName,
        properties: eventData,
      });
    }
  } else {
    // Client-side tracking
    const posthog = getPostHog();
    if (posthog) {
      posthog.capture(eventName, eventData);
    }
  }
};

// Track API call events
export const trackAPICall = async (endpoint: string, properties: {
  userId?: number;
  userEmail?: string;
  userRole?: string;
  planId?: string;
  creditsUsed?: number;
  responseStatus?: number;
  ipAddress?: string;
  apiKeyId?: string;
  requestDuration?: number;
  userAgent?: string;
  creditsRemaining?: number;
  lastActive?: string;
}) => {
  // Create event name from endpoint
  const eventName = `api_called_${endpoint.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}`;
  
  // Categorize response status
  const getResponseStatusCategory = (status?: number): string => {
    if (!status) return 'Unknown';
    if (status >= 200 && status < 300) return 'Success';
    if (status >= 300 && status < 400) return 'Redirect';
    if (status >= 400 && status < 500) return 'Client Error';
    if (status >= 500) return 'Server Error';
    return 'Unknown';
  };

  const responseStatusCategory = getResponseStatusCategory(properties.responseStatus);
  
  // Update person properties with latest info
  if (properties.userId && properties.userEmail) {
    await updatePersonProperties(properties.userId, {
      email: properties.userEmail,
      role: properties.userRole,
      planId: properties.planId,
      creditsRemaining: properties.creditsRemaining,
      ipAddress: properties.ipAddress,
      userAgent: properties.userAgent,
      lastActive: properties.lastActive || new Date().toISOString(),
    });
  }
  
  await trackAPIEvent(eventName, {
    endpoint,
    ...properties,
    event_type: 'api_call',
    responseStatusCategory,
  });
};

// Track API errors
export const trackAPIError = (endpoint: string, error: string, properties: {
  userId?: number;
  userEmail?: string;
  ipAddress?: string;
  apiKeyId?: string;
  responseStatus?: number;
}) => {
  // Categorize response status for errors
  const getResponseStatusCategory = (status?: number): string => {
    if (!status) return 'Unknown';
    if (status >= 200 && status < 300) return 'Success';
    if (status >= 300 && status < 400) return 'Redirect';
    if (status >= 400 && status < 500) return 'Client Error';
    if (status >= 500) return 'Server Error';
    return 'Unknown';
  };

  const responseStatusCategory = getResponseStatusCategory(properties.responseStatus);

  trackAPIEvent('api_error', {
    endpoint,
    error,
    ...properties,
    event_type: 'api_error',
    responseStatusCategory,
  });
};

// Track rate limit hits
export const trackRateLimitHit = (endpoint: string, properties: {
  userId?: number;
  userEmail?: string;
  ipAddress?: string;
  planId?: string;
  maxRequestsPerMinute?: number;
  responseStatus?: number;
}) => {
  // Categorize response status for rate limit hits
  const getResponseStatusCategory = (status?: number): string => {
    if (!status) return 'Unknown';
    if (status >= 200 && status < 300) return 'Success';
    if (status >= 300 && status < 400) return 'Redirect';
    if (status >= 400 && status < 500) return 'Client Error';
    if (status >= 500) return 'Server Error';
    return 'Unknown';
  };

  const responseStatusCategory = getResponseStatusCategory(properties.responseStatus);

  trackAPIEvent('api_rate_limit_hit', {
    endpoint,
    ...properties,
    event_type: 'rate_limit',
    responseStatusCategory,
  });
};

// Track credit exhaustion
export const trackCreditExhaustion = (properties: {
  userId?: number;
  userEmail?: string;
  planId?: string;
  creditsRemaining?: number;
  creditsUsed?: number;
  responseStatus?: number;
}) => {
  // Categorize response status for credit exhaustion
  const getResponseStatusCategory = (status?: number): string => {
    if (!status) return 'Unknown';
    if (status >= 200 && status < 300) return 'Success';
    if (status >= 300 && status < 400) return 'Redirect';
    if (status >= 400 && status < 500) return 'Client Error';
    if (status >= 500) return 'Server Error';
    return 'Unknown';
  };

  const responseStatusCategory = getResponseStatusCategory(properties.responseStatus);

  trackAPIEvent('api_credits_exhausted', {
    ...properties,
    event_type: 'credit_exhaustion',
    responseStatusCategory,
  });
};

// Track plan upgrades/downgrades
export const trackPlanChange = (properties: {
  userId?: number;
  userEmail?: string;
  fromPlan?: string;
  toPlan?: string;
  action?: 'upgrade' | 'downgrade' | 'no_change';
}) => {
  trackAPIEvent('plan_changed', {
    ...properties,
    event_type: 'plan_change',
  });
};

// Update person properties (server-side)
export const updatePersonProperties = async (userId: number, properties: {
  email?: string;
  name?: string;
  role?: string;
  planId?: string;
  creditsRemaining?: number;
  ipAddress?: string;
  userAgent?: string;
  lastActive?: string;
}) => {
  if (typeof window === 'undefined') {
    // Server-side tracking
    const serverPostHog = await getServerPostHog();
    if (serverPostHog && properties.email) {
      const distinctId = properties.email;
      serverPostHog.capture({
        distinctId: distinctId,
        event: '$set',
        properties: {
          // Basic user info
          userId: userId,
          email: properties.email,
          name: properties.name,
          role: properties.role,
          
          // Plan and billing info
          planId: properties.planId,
          creditsRemaining: properties.creditsRemaining,
          
          // Technical info
          ipAddress: properties.ipAddress,
          userAgent: properties.userAgent,
          
          // Activity info
          lastActive: properties.lastActive,
          
          // System info
          platform: 'web',
          environment: process.env.NODE_ENV || 'development',
        }
      });
    }
  } else {
    // Client-side tracking
    const posthog = getPostHog();
    if (posthog && properties.email) {
      const distinctId = properties.email;
      posthog.identify(distinctId, {
        userId: userId,
        email: properties.email,
        name: properties.name,
        role: properties.role,
        planId: properties.planId,
        creditsRemaining: properties.creditsRemaining,
        ipAddress: properties.ipAddress,
        userAgent: properties.userAgent,
        lastActive: properties.lastActive,
        platform: 'web',
        environment: process.env.NODE_ENV || 'development',
      });
    }
  }
};

// Identify user for PostHog
export const identifyUser = (userId: number, properties: {
  email?: string;
  name?: string;
  role?: string;
  planId?: string;
  creditsRemaining?: number;
  ipAddress?: string;
  userAgent?: string;
  lastActive?: string;
  createdAt?: string;
}) => {
  const posthog = getPostHog();
  if (posthog) {
    // Use email as primary identifier if available, fallback to user ID
    const distinctId = properties.email || userId.toString();
    posthog.identify(distinctId, {
      // Basic user info
      userId: userId,
      email: properties.email,
      name: properties.name,
      role: properties.role,
      
      // Plan and billing info
      planId: properties.planId,
      creditsRemaining: properties.creditsRemaining,
      
      // Technical info
      ipAddress: properties.ipAddress,
      userAgent: properties.userAgent,
      
      // Activity info
      lastActive: properties.lastActive,
      createdAt: properties.createdAt,
      
      // System info
      platform: 'web',
      environment: process.env.NODE_ENV || 'development',
    });
  }
};

/**
 * Tie an anonymous visitor to the email they just submitted in a lead form, so their
 * earlier pageviews land on the same person. Never re-identifies a signed-in user.
 */
export const identifyLead = (email: string | null | undefined, properties: Record<string, any> = {}) => {
  const posthog = getPostHog();
  const normalized = email?.trim().toLowerCase();
  if (!posthog || !normalized) return;
  try {
    if (posthog.get_property?.('$user_state') === 'identified') {
      posthog.setPersonProperties?.({}, { first_lead_email: normalized });
      return;
    }
    posthog.identify(normalized, { email: normalized, ...properties });
  } catch {}
};

/** Forget the current person on sign-out so the next visitor on this browser starts fresh. */
export const resetUser = () => {
  try {
    getPostHog()?.reset();
  } catch {}
};

// Track page views
export const trackPageView = (page: string, properties?: Record<string, any>) => {
  trackAPIEvent('page_viewed', {
    page,
    ...properties,
    event_type: 'page_view',
  });
};
